import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { AppRoutePath } from 'src/app/globals/app-routepath';

/**
 * Guard that prevents authenticated users from accessing guest-only pages
 * (e.g., login, forgot-password). If the user is already logged in, they
 * are redirected to the appropriate dashboard based on their role.
 *
 * Works for both normal login and Google SSO login since authentication
 * state is determined by the presence of a valid JWT in localStorage.
 */
export const noAuthGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): boolean => {
  const router = inject(Router);
  const authService = inject(AuthService);

  if (authService.isAuthenticated()) {
    const userDetails: any = authService.getUserDetails();

    // If token is invalid/expired, getUserDetails() returns false and
    // clears localStorage — allow access to the login page in that case.
    if (!userDetails) {
      return true;
    }

    const userRoles: string[] = Array.isArray(userDetails?.userRole)
      ? userDetails.userRole
      : typeof userDetails?.userRole === 'string'
        ? userDetails.userRole.split(',').map((r: string) => r.trim())
        : [];

    // Panel users go to the technical station; everyone else goes to dashboard
    if (userRoles.includes('panel') && userRoles.length === 1) {
      router.navigate([`${AppRoutePath.TECHNICAL.ROOT}/2`]);
    } else {
      router.navigate([AppRoutePath.DASHBOARD.ROOT]);
    }

    return false;
  }

  return true;
};
